import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import store from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_WARNING_MS = 25 * 60 * 1000;

export const useSessionManagement = () => {
  const { isAuthenticated, logout } = useAuth0();
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetIdleTimeout = () => {
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

    if (!isAuthenticated) {
      return;
    }

    warningTimeoutRef.current = setTimeout(() => {
      store.dispatch(showFeedbackModal({
        showModal: true,
        message: 'Your session will expire in 5 minutes due to inactivity.',
        modalType: 'warning',
        duration: 5000
      }));
    }, IDLE_WARNING_MS);

    activityTimeoutRef.current = setTimeout(() => {
      localStorage.clear();
      sessionStorage.clear();

      store.dispatch(showFeedbackModal({
        showModal: true,
        message: 'Your session has expired. Please log in again.',
        modalType: 'warning',
        duration: 3000
      }));

      logout({ logoutParams: { returnTo: window.location.origin } });
    }, IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      return;
    }

    resetIdleTimeout();

    const handleUserActivity = () => {
      resetIdleTimeout();
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => document.addEventListener(event, handleUserActivity));

    return () => {
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      events.forEach(event => document.removeEventListener(event, handleUserActivity));
    };
  }, [isAuthenticated, logout]);
};

export default useSessionManagement;
